import type { ObjectId } from 'mongodb';

import type { CongaSignerRole } from './CongaTestEnvelopes';

import type { Locale } from './index';

/** The signers who go through the landing page; the installer and sales rep only sign inside Conga. */
export const CONGA_CUSTOMER_SIGNER_ROLES = ['PrimaryApplicant', 'CoApplicant'] as const;
export type CongaCustomerSignerRole = Extract<CongaSignerRole, (typeof CONGA_CUSTOMER_SIGNER_ROLES)[number]>;

/** Set once, by whichever customer picks English or Spanish first; it never changes afterwards. */
export interface CongaContractLanguageChoice {
  language: Locale;
  setByRole: CongaCustomerSignerRole;
  /** The pre-signing record that won the write, so a racing writer can tell it lost. */
  setByRecordId: ObjectId;
  setAt: Date;
}

export const CONGA_LANGUAGE_OTHER = 'other';
export type CongaLanguageOption = Locale | typeof CONGA_LANGUAGE_OTHER;

export interface CongaLanguagePreference {
  choice: CongaLanguageOption;
  /** Free text the customer typed when they picked "Other". */
  otherLanguage?: string;
  statedAt: Date;
}

/** Values exactly as they go into the Conga v6 Contract fields of the same name. */
export interface CongaKeyTerms {
  yearOneMonthlyPayment: string;
  annualEscalatorRate: string;
  agreementTermYears: number | null;
}

export interface CongaKeyTermsAcknowledgement {
  values: CongaKeyTerms;
  language: Locale;
  acknowledgedAt: Date;
}

export interface CongaConsentDisclosureRef {
  disclosureId: ObjectId;
  version: string;
  language: Locale;
}

export interface CongaSigningConsent {
  /** Spanish signers agree to the Spanish text with the English text alongside it, so both are recorded. */
  disclosures: CongaConsentDisclosureRef[];
  language: Locale;
  consentedAt: Date;
  ipAddress: string;
  userAgent: string;
  /** The account document holding the exact text agreed to; set by the consent document worker. */
  documentId?: ObjectId;
}

/** One per customer signer per contract. Never copied between contracts, even for the same person. */
export interface CongaPresigningRecordDocument {
  id: ObjectId;
  accountId: ObjectId;
  contractId: ObjectId;
  inviteId: ObjectId;
  role: CongaCustomerSignerRole;
  email: string;
  name: string;
  /** Bearer link: never serialized to API responses, logs or events. */
  landingUrl: string;
  languagePreference?: CongaLanguagePreference;
  /** The contract language this signer continued in, from screen 1 or 1b. */
  contractLanguageSeen?: Locale;
  englishOnlyNoticeAcknowledgedAt?: Date;
  keyTerms?: CongaKeyTermsAcknowledgement;
  consent?: CongaSigningConsent;
  completedAt?: Date;
}

export const CONGA_PRESIGNING_STEPS = ['language', 'englishOnlyNotice', 'keyTerms', 'consent', 'complete'] as const;
export type CongaPresigningStep = (typeof CONGA_PRESIGNING_STEPS)[number];

export interface CongaPresigningConsentText {
  disclosureId: string;
  version: string;
  language: Locale;
  text: string;
  acceptanceLabel: string;
}

/** What the landing page renders for one signer. */
export interface CongaPresigningState {
  role: CongaCustomerSignerRole;
  firstName: string;
  step: CongaPresigningStep;
  /** Null until the first customer picks English or Spanish. */
  contractLanguage: Locale | null;
  languagePreference: { choice: CongaLanguageOption; otherLanguage?: string } | null;
  englishOnlyNoticeRequired: boolean;
  /** Present from the key terms step on. */
  keyTerms: CongaKeyTerms | null;
  /** Present from the consent step on; Spanish lists the Spanish text first, then the English one. */
  consentDisclosures: CongaPresigningConsentText[] | null;
}

export type CongaPresigningSigningUrlResult =
  | { status: 'ready'; url: string }
  | { status: 'waiting' }
  | { status: 'unavailable'; reason: 'presigningIncomplete' | 'failed' | 'notSigner' | 'notSubmitted' };
